/**
 * MessagingPage — Bloomberg-style internal chat via Matrix Synapse.
 *
 * Layout:
 *   Left panel  — room list
 *   Right panel — scrollable message thread + input bar
 *
 * Updates poll every 5 s; WebSocket upgrade is a future task.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import {
  MessageSquare, Hash, Send, RefreshCw, Plus, X, Loader2, User,
} from 'lucide-react'
import { format } from 'date-fns'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Room {
  room_id: string
  name: string
}

interface Message {
  event_id: string
  sender: string
  body: string
  timestamp: number
  financegenie_sender: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function displayName(sender: string): string {
  // @simon:financegenie.bullers.com  →  Simon
  const local = sender.startsWith('@') ? sender.slice(1).split(':')[0] : sender
  return local.charAt(0).toUpperCase() + local.slice(1)
}

function senderColour(sender: string): string {
  const colours: Record<string, string> = {
    simon: 'text-blue-light',
    unman: 'text-emerald',
    aria:  'text-amber-400',
  }
  const local = ((sender ?? '').startsWith('@') ? (sender ?? '').slice(1).split(':')[0] : (sender ?? '')).toLowerCase()
  return colours[local] ?? 'text-text-secondary'
}

function avatarInitial(sender: string): string {
  const local = sender.startsWith('@') ? sender.slice(1).split(':')[0] : sender
  return local.charAt(0).toUpperCase()
}

function formatTs(ts: number): string {
  return format(new Date(ts), 'HH:mm')
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RoomItem({
  room,
  active,
  onClick,
}: {
  room: Room
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors',
        active
          ? 'bg-blue/10 text-blue-light font-medium'
          : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
      )}
    >
      <Hash size={14} className="shrink-0" />
      <span className="truncate">{room.name}</span>
    </button>
  )
}

function MessageBubble({ msg }: { msg: Message }) {
  return (
    <div className="flex items-start gap-3 group">
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-surface-card border border-border flex items-center justify-center shrink-0">
        <span className={clsx('text-xs font-bold', senderColour(msg.sender))}>
          {avatarInitial(msg.financegenie_sender || msg.sender)}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className={clsx('text-sm font-semibold', senderColour(msg.sender))}>
            {displayName(msg.financegenie_sender || msg.sender)}
          </span>
          <span className="text-xs text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
            {formatTs(msg.timestamp)}
          </span>
        </div>
        <p className="text-sm text-text-primary leading-relaxed break-words">{msg.body}</p>
      </div>
    </div>
  )
}

// ── CreateRoomModal ───────────────────────────────────────────────────────────

function CreateRoomModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (room: Room) => void
}) {
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setLoading(true)
    setError(null)
    try {
      // Derive room members from the real household profile — no hardcoded
      // username list. If the profile is unavailable, fall back to an empty
      // invite list (the backend still creates the room for the caller).
      let members: string[] = []
      try {
        const prof = await api.get('/api/profile')
        members = (prof.data?.members ?? [])
          .map((m: { name?: string | null }) => String(m?.name ?? '').trim().toLowerCase())
          .filter(Boolean)
      } catch {
        members = []
      }
      const { data } = await api.post('/api/messaging/rooms', {
        name: name.trim(),
        members,
      })
      onCreated({ room_id: data.room_id, name: name.trim() })
      onClose()
    } catch {
      setError('Failed to create room. Is Synapse running?')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="card p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="panel-title">New Room</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-card text-text-muted">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. portfolio-review"
            className="input px-3 py-2"
          />
          {error && <p className="text-xs text-rose-light">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost px-3 py-1.5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="btn-primary px-4 py-1.5"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MessagingPage() {
  const [rooms, setRooms] = useState<Room[]>([])
  const [activeRoom, setActiveRoom] = useState<Room | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loadingRooms, setLoadingRooms] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sending, setSending] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Fetch rooms ─────────────────────────────────────────────────────────

  const fetchRooms = useCallback(async () => {
    setLoadingRooms(true)
    setError(null)
    try {
      const { data } = await api.get<Room[]>('/api/messaging/rooms')
      setRooms(data)
      if (data.length > 0 && !activeRoom) {
        setActiveRoom(data[0])
      }
    } catch {
      setError('Cannot reach messaging server. Synapse may not be configured yet.')
    } finally {
      setLoadingRooms(false)
    }
  }, [activeRoom])

  // ── Fetch messages ───────────────────────────────────────────────────────

  const fetchMessages = useCallback(async (room: Room) => {
    setLoadingMessages(true)
    try {
      const { data } = await api.get<Message[]>(
        `/api/messaging/rooms/${encodeURIComponent(room.room_id)}/messages`,
        { params: { limit: 50 } },
      )
      setMessages(data)
    } catch {
      // Silently fail during polling — show error only on first load
    } finally {
      setLoadingMessages(false)
    }
  }, [])

  // ── Auto-scroll ──────────────────────────────────────────────────────────

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Load rooms on mount ──────────────────────────────────────────────────

  useEffect(() => {
    fetchRooms()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load + poll messages when room changes ───────────────────────────────

  useEffect(() => {
    if (!activeRoom) return
    fetchMessages(activeRoom)

    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(() => fetchMessages(activeRoom), 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [activeRoom, fetchMessages])

  // ── Send message ─────────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = input.trim()
    if (!text || !activeRoom || sending) return
    setSending(true)
    setInput('')
    try {
      await api.post('/api/messaging/send', {
        room_id: activeRoom.room_id,
        message: text,
      })
      // Immediately re-fetch to show the sent message
      await fetchMessages(activeRoom)
    } catch {
      setInput(text) // restore on failure
      setError('Failed to send message.')
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ── Room created callback ────────────────────────────────────────────────

  const handleRoomCreated = (room: Room) => {
    setRooms(prev => [...prev, room])
    setActiveRoom(room)
    setMessages([])
  }

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      {showCreate && (
        <CreateRoomModal onClose={() => setShowCreate(false)} onCreated={handleRoomCreated} />
      )}

      {/* ── Left: Room list ─────────────────────────────────────────────── */}
      <div className="w-56 shrink-0 border-r border-border flex flex-col bg-surface">
        <div className="glass px-4 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-blue-light" />
            <span className="text-sm font-semibold text-text-primary">Messages</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={fetchRooms}
              disabled={loadingRooms}
              className="p-1 rounded hover:bg-surface-card text-text-muted transition-colors"
              title="Refresh rooms"
            >
              <RefreshCw size={13} className={clsx(loadingRooms && 'animate-spin')} />
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="p-1 rounded hover:bg-surface-card text-text-muted transition-colors"
              title="New room"
            >
              <Plus size={13} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {loadingRooms && rooms.length === 0 && (
            <div className="space-y-2 py-2 px-1">
              <div className="h-8 skeleton" />
              <div className="h-8 skeleton" />
              <div className="h-8 skeleton" />
            </div>
          )}
          {!loadingRooms && rooms.length === 0 && !error && (
            <p className="text-xs text-text-muted text-center py-8 px-2">
              No rooms yet.{' '}
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="text-blue-light underline"
              >
                Create one
              </button>
            </p>
          )}
          {rooms.map(room => (
            <RoomItem
              key={room.room_id}
              room={room}
              active={activeRoom?.room_id === room.room_id}
              onClick={() => {
                setActiveRoom(room)
                setMessages([])
              }}
            />
          ))}
        </div>

        {/* Setup hint */}
        <div className="px-3 py-3 border-t border-border">
          <p className="text-[10px] text-text-muted leading-tight">
            Run{' '}
            <code className="font-mono bg-surface-card px-1 py-0.5 rounded text-blue-light">
              POST /api/messaging/setup
            </code>{' '}
            to initialise default rooms.
          </p>
        </div>
      </div>

      {/* ── Right: Messages ────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 bg-surface">
        {/* Header */}
        <div className="glass px-6 py-4 border-b border-border flex items-center gap-3 shrink-0">
          {activeRoom ? (
            <>
              <Hash size={16} className="text-text-muted shrink-0" />
              <span className="font-semibold text-text-primary">{activeRoom.name}</span>
            </>
          ) : (
            <span className="text-sm text-text-muted">Select a room</span>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-rose/10 border border-rose/20 text-sm text-rose-light">
            {error}
          </div>
        )}

        {/* Messages thread */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
          {loadingMessages && messages.length === 0 && (
            <div className="space-y-4 py-4">
              <div className="h-12 w-2/3 skeleton" />
              <div className="h-12 w-1/2 skeleton" />
              <div className="h-12 w-3/5 skeleton" />
            </div>
          )}

          {!loadingMessages && activeRoom && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-12 h-12 rounded-full bg-surface-card border border-border flex items-center justify-center">
                <MessageSquare size={20} className="text-text-muted" />
              </div>
              <p className="text-sm text-text-muted">No messages yet. Say hello!</p>
            </div>
          )}

          {messages.map(msg => (
            <MessageBubble key={msg.event_id} msg={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div className="px-4 py-4 border-t border-border shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-card border border-border
                            focus-within:ring-2 focus-within:ring-blue/40 transition-all">
              <User size={14} className="text-text-muted shrink-0" />
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  activeRoom ? `Message #${activeRoom.name}` : 'Select a room first'
                }
                disabled={!activeRoom || sending}
                className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted
                           focus:outline-none disabled:cursor-not-allowed"
              />
            </div>
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || !activeRoom || sending}
              className="btn-primary p-2.5"
              title="Send (Enter)"
            >
              {sending
                ? <Loader2 size={16} className="animate-spin" />
                : <Send size={16} />}
            </button>
          </div>
          <p className="text-[10px] text-text-muted mt-1.5 ml-1">Enter to send</p>
        </div>
      </div>
    </div>
  )
}
